import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, OnInit, ViewChild, effect, inject, input, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';

import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { catchError, finalize, map, Observable, of, tap } from 'rxjs';

import { SvcHighlightGroupModule } from '@solvace/business-component/forms/svc-highlight-group';
import { SvcControlsModule, ISvcChipGroupedOption, ISvcChipOption } from '@solvace/component/svc-controls';
import { SvcDialogMode } from '@solvace/component/svc-dialogs';
import { SvcAppSettings } from 'projects/lib-shared-common/src/public-api';
import { SvcSlideToggleModule } from '@solvace/component/svc-slide-toggle';
import { SvcStatusBadgeModule } from '@solvace/component/svc-status-badge';
import { SvcCustomFieldsRegistrationFeature } from '@solvace/feature/admin/svc-custom-fields-registration/enums/svc-custom-fields-registration.enum';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { SvcAudioToTextButtonModule } from '@solvace/feature/general/svc-audio-to-text-button';
import { SvcFormCustomFieldComponent, SvcFormCustomFieldModule } from '@solvace/feature/general/svc-form-custom-field';
import { SvcSmartUserAvatarModule } from '@solvace/feature/general/svc-smart-user-avatar';
import { LibSharedCommonModule } from 'projects/lib-shared-common/src/public-api';
import { HttpErrorService } from 'projects/lib-shared-common/src/lib/services/http-error.service';
import { StaticApplicationId, UserService } from 'projects/lib-shared-core/src/public-api';

import {
  SvcModalActionPlanByIdResponse,
  SvcModalActionPlanCombos,
  SvcModalActionPlanCopyHeaderData,
  SvcModalActionPlanCreateRequest,
  SvcModalActionPlanCreateResponse,
  SvcModalActionPlanNotificationParams,
  SvcModalActionPlanSystemData,
} from '../../interfaces/svc-modal-action-plan.interface';
import {
  findSystemByApplicationId,
  resolveWidgetSourceLabel,
} from '../../utils/actionplan-notification-params.utils';
import { isDraftSourceRef, parseActionPlanSourceRegistry } from '../../utils/actionplan-source-registry.utils';
import { SvcActionPlanSourceRedirectService } from '../../services/svc-action-plan-source-redirect.service';
import { SvcSourceLinkRedirectService } from '../../../../../general/svc-source-link/svc-source-link-redirect.service';
import { SvcModalActionPlanService } from '../../services/svc-modal-action-plan.service';
import { SvcActionPlanPermissionService } from '../../services/svc-action-plan-permission.service';
import {
  SvcModalActionPlanStatusId,
  SvcModalActionPlanStatusLabelById,
  SvcModalActionPlanStatusTypeById,
} from '../../enums/svc-modal-action-plan-status.enum';
import {
  decodeOwnerSelection,
  encodeOwnerSelection,
  isTeamOwnerValue,
  stripOwnerPrefix,
} from '../../utils/actionplan-owner-selection.utils';

@Component({
  selector: 'svc-action-plan-form',
  standalone: true,
  imports: [
    CommonModule,
    LibSharedCommonModule,
    ReactiveFormsModule,
    TranslocoModule,
    MatIconModule,
    SvcControlsModule,
    SvcSlideToggleModule,
    SvcAudioToTextButtonModule,
    SvcHighlightGroupModule,
    SvcFormCustomFieldModule,
    SvcSmartUserAvatarModule,
    SvcStatusBadgeModule,
    NgxSkeletonLoaderModule,
  ],
  templateUrl: './svc-action-plan-form.component.html',
  styleUrls: ['./svc-action-plan-form.component.scss'],
  providers: [DatePipe],
})
export class SvcActionPlanFormComponent implements OnInit {
  @ViewChild('customField') public customField?: SvcFormCustomFieldComponent;

  public readonly mode = model<SvcDialogMode>(SvcDialogMode.add);
  public readonly notificationParams = input<SvcModalActionPlanNotificationParams | undefined>(undefined);
  public readonly combosLoaded = output<void>();

  #destroyRef = inject(DestroyRef);
  #formBuilder = inject(FormBuilder);
  #datePipe = inject(DatePipe);
  #appSettings = inject(SvcAppSettings);
  #userService = inject(UserService);
  #errorService = inject(HttpErrorService);
  #modalActionPlanService = inject(SvcModalActionPlanService);
  #permissions = inject(SvcActionPlanPermissionService);
  #translocoService = inject(TranslocoService);
  #sourceRedirect = inject(SvcActionPlanSourceRedirectService);
  #sourceLinkRedirect = inject(SvcSourceLinkRedirectService);

  public readonly modalModes = SvcDialogMode;
  public readonly statusLabelById = SvcModalActionPlanStatusLabelById;
  public readonly statusTypeById = SvcModalActionPlanStatusTypeById;

  public showVoiceToTextButton = true;
  public customFieldsRefreshTick = signal<number>(0);
  public dateFieldsRefreshTick = signal<number>(0);
  public isAllCombosLoading = false;
  public formActionPlan!: FormGroup;
  public optionsCombos: SvcModalActionPlanCombos = {
    types: [],
    priorities: [],
    systems: [],
    users: [],
    teams: [],
    owners: [],
  };
  public originatorName = '';
  public selectedSystemTemplateId: number | null = null;
  public selectedSystemUniqueId: number | null = null;
  public customFieldsFeatureId = SvcCustomFieldsRegistrationFeature.actionPlan;
  public applicationId = StaticApplicationId.actionPlanRevamp;
  public canEditOriginator = false;
  public actionPlanUniqueId: number | null = null;
  public dueDateMin?: Date;
  public actionPlanNumber = `${this.#translocoService.translate('PLA')}-`;
  public sourceLabel = '-';
  public showSourceLink = false;
  public canOpenSourceLink = false;
  public reprogrammingLabel = '-';
  public isSystemLockedInEditMode = false;
  public isSystemLockedByWidget = false;
  public shouldShowCustomFields = false;
  /**
   * Bridges the gap between "system selected" and "we know whether the system
   * has a custom-field template": while `isCustomFieldTemplateActive` is in
   * flight the template renders a skeleton so the area never goes blank.
   */
  public isResolvingCustomFieldsTemplate = false;
  #lastPatchedActionPlan: SvcModalActionPlanByIdResponse | null = null;
  #lastRequestedSystemUniqueId: number | null = null;
  #lastTemplateValidationSystemUniqueId: number | null = null;
  #selectedSystemHasTemplate = false;
  #resolvedSystemLabel = '-';
  #apiOriginatorName = '';
  #apiResponsibleName = '';
  #suppressedCustomFieldReloads = 0;
  #lockedSystemApplicationId: string | null = null;
  #lockedSystemUniqueId: number | null = null;

  constructor() {
    effect(() => {
      const _ = this.mode();
      if (this.formActionPlan) {
        this.#applyModeRules();
      }
    });
  }

  public ngOnInit(): void {
    this.#initForm();
    this.#setOriginatorDisplayName();
    this.#setOriginatorPermission();
    this.#loadCombos();
    this.#onSystemChange();
    this.#applyModeRules();
  }

  public get isAddMode(): boolean {
    return this.mode() === this.modalModes.add;
  }

  public get isEditMode(): boolean {
    return this.mode() === this.modalModes.edit;
  }

  public get currentStatusId(): number | null {
    return this.formActionPlan?.controls?.statusId?.value ?? null;
  }

  public get hasStatusTag(): boolean {
    return !!this.currentStatusId;
  }

  public get reprogrammingFieldLabel(): string {
    const translatedLabel = this.#translocoService.translate('Reprogramação');
    return translatedLabel === 'Rescheduling' ? 'Reprogramming' : translatedLabel;
  }

  public get selectedTypeLabel(): string {
    const selectedTypeLabel = this.#resolveOptionLabel(
      this.#resolveSingleSelectionValue(this.formActionPlan?.getRawValue()?.typeId, this.#lastPatchedActionPlan?.typeId),
      this.optionsCombos?.types
    );
    return selectedTypeLabel !== '-' ? selectedTypeLabel : this.#resolveTypeFallbackLabel(this.#lastPatchedActionPlan?.typeId);
  }

  public get selectedPriorityLabel(): string {
    const selectedPriorityLabel = this.#resolveOptionLabel(
      this.#resolveSingleSelectionValue(this.formActionPlan?.getRawValue()?.priorityId, this.#lastPatchedActionPlan?.priorityUniqueId),
      this.optionsCombos?.priorities
    );
    return selectedPriorityLabel !== '-' ? selectedPriorityLabel : this.#resolvePriorityFallbackLabel(this.#lastPatchedActionPlan?.priorityUniqueId);
  }

  public get selectedSystemLabel(): string {
    const selectedSystemLabel = this.#resolveOptionLabel(
      this.#resolveSingleSelectionValue(this.formActionPlan?.getRawValue()?.systemId, this.#lastPatchedActionPlan?.systemUniqueId),
      this.optionsCombos?.systems
    );
    return selectedSystemLabel !== '-' ? selectedSystemLabel : this.#resolvedSystemLabel;
  }

  public get selectedResponsibleId(): string | null {
    const resolvedValue = this.#resolveSingleSelectionValue(
      this.formActionPlan?.getRawValue()?.responsibleId,
      this.#lastPatchedActionPlan?.responsibleUserId
    );
    return resolvedValue ? String(resolvedValue) : null;
  }

  public get selectedResponsibleLabel(): string {
    return this.#resolveUserLabel(this.selectedResponsibleId) || this.#apiResponsibleName || '-';
  }

  /**
   * Encoded selection used by the read-only Atores block. Each entry is
   * either `user:<guid>` or `team:<id>` — the template uses {@link isActorTeamValue}
   * and {@link getActorRawId} to decide between rendering a user avatar
   * and a team icon.
   */
  public get selectedActorIds(): string[] {
    const rawActors = this.formActionPlan?.getRawValue()?.actors;
    const resolved = Array.isArray(rawActors) && rawActors.length
      ? rawActors
      : this.#resolveEncodedOwnersFromActionPlan(this.#lastPatchedActionPlan);
    return this.#normalizeActorIds(resolved ?? []);
  }

  /**
   * Display labels aligned 1-to-1 with {@link selectedActorIds}. Users
   * resolve through the users combo; teams resolve through the teams combo.
   * Empty entries are kept so the index alignment with the encoded value
   * list is preserved (template renders `'-'` for blanks).
   */
  public get selectedActorLabels(): string[] {
    return this.selectedActorIds.map((encoded) => {
      const rawId = stripOwnerPrefix(encoded);
      if (isTeamOwnerValue(encoded)) {
        return this.#resolveTeamLabel(rawId);
      }
      return this.#resolveUserLabel(rawId) || '';
    });
  }

  /** True when the encoded actor entry points to a team (not a user). */
  public isActorTeamValue(value: string | number | null | undefined): boolean {
    return isTeamOwnerValue(value);
  }

  /** Strips the `user:` / `team:` prefix and returns the raw id. */
  public getActorRawId(value: string | number | null | undefined): string {
    return stripOwnerPrefix(value);
  }

  #initForm(): void {
    const initialOriginatorId = this.isAddMode ? [this.#userService.userId$] : [];
    this.formActionPlan = this.#formBuilder.group({
      description: [null, [Validators.required, Validators.maxLength(150)]],
      statusId: [null],
      typeId: [[], Validators.required],
      priorityId: [[], Validators.required],
      systemId: [[], Validators.required],
      originatorId: [initialOriginatorId, Validators.required],
      responsibleId: [[], Validators.required],
      actors: [[]],
      confidential: [false],
      approverIds: [[]],
      creationDate: [{ value: this.#datePipe.transform(new Date(), 'yyyy-MM-dd'), disabled: true }],
      dueDate: [null, Validators.required],
      approvalDateText: [{ value: '-', disabled: true }],
      conclusionDateText: [{ value: '-', disabled: true }],
    });
  }

  #setOriginatorPermission(): void {
    const user = this.#userService.user;
    const roleNames = ['administrador', 'administrator'];
    const applicationIds = [
      StaticApplicationId.actionPlanRevamp.toUpperCase(),
      StaticApplicationId.actionPlan.toUpperCase(),
      StaticApplicationId.actionPlanCore.toUpperCase(),
    ];

    const hasAdminRole = (user?.roles ?? []).some((role) => {
      const appId = String(role?.applicationId ?? '').toUpperCase();
      const roleName = String(role?.roleName ?? '').toLowerCase();
      return applicationIds.includes(appId) && roleNames.includes(roleName);
    });

    this.canEditOriginator = !!user?.isSolvaceAdmin || hasAdminRole;
  }

  #setOriginatorDisplayName(): void {
    // Same rationale as #initForm: only ADD mode shows the logged-in user as
    // originator up-front; VIEW/EDIT waits for the API payload to populate
    // the name, so the user never sees their own name flash for a row that
    // was originated by someone else.
    this.originatorName = this.isAddMode ? (this.#userService.user?.firstLastName ?? '') : '';
  }

  #loadCombos(): void {
    this.isAllCombosLoading = true;
    this.#modalActionPlanService
      .getCombos()
      .pipe(
        tap((combos) => {
          this.optionsCombos = combos;
          const wasDisabled = !!this.formActionPlan?.disabled;
          if (wasDisabled) {
            this.formActionPlan.enable({ emitEvent: false });
          }
          this.#syncSelectionControlsWithLoadedOptions();
          this.#applyNotificationParams();
          if (wasDisabled) {
            this.#applyModeRules();
          }
          this.#setSystemTemplateId(this.formActionPlan?.controls?.systemId?.value);
          this.#syncOriginatorDisplayFromForm();
          this.#loadCustomFieldsByActionPlanId();
          this.#applyLockedSystem();
          this.combosLoaded.emit();
        }),
        catchError((err) => {
          this.#errorService.showErrorInToast(err);
          return of(null);
        }),
        finalize(() => (this.isAllCombosLoading = false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  #onSystemChange(): void {
    this.formActionPlan?.controls?.systemId?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe({
      next: (systemId: number[] | number | string | null) => {
        const resolvedId = this.#resolveSelectedSystemId(systemId);
        if (resolvedId === this.selectedSystemUniqueId) return;
        this.#setSystemTemplateId(systemId);
      },
    });
  }

  public onSystemSelectChanged(event: { option: ISvcChipOption<SvcModalActionPlanSystemData>; selected: boolean }): void {
    if (!event?.selected) {
      this.#setSystemTemplateId(this.formActionPlan?.controls?.systemId?.value ?? null);
      return;
    }

    const selectedSystemId = this.#resolveSelectedSystemId(event?.option?.value ?? null);
    if (!selectedSystemId) {
      this.selectedSystemUniqueId = null;
      this.selectedSystemTemplateId = null;
      return;
    }

    this.selectedSystemUniqueId = selectedSystemId;

    const templateIdFromOption = Number(event?.option?.data?.templateId ?? null);
    if (Number.isFinite(templateIdFromOption) && templateIdFromOption > 0) {
      this.selectedSystemTemplateId = templateIdFromOption;
      this.#selectedSystemHasTemplate = true;
      this.#validateTemplateAndRefreshCustomFields(selectedSystemId, this.selectedSystemTemplateId);
      return;
    }

    this.#setSystemTemplateId(selectedSystemId);
  }

  #setSystemTemplateId(systemControlValue: number[] | number | string | null): void {
    const selectedSystemId = this.#resolveSelectedSystemId(systemControlValue);
    if (!selectedSystemId) {
      this.selectedSystemUniqueId = null;
      this.selectedSystemTemplateId = null;
      this.#selectedSystemHasTemplate = false;
      this.shouldShowCustomFields = false;
      this.#resolvedSystemLabel = '-';
      return;
    }

    const selectedSystem = this.optionsCombos?.systems?.find((option) => `${option?.value}` === `${selectedSystemId}`);
    this.selectedSystemUniqueId = selectedSystemId;
    this.#resolvedSystemLabel = String(selectedSystem?.data?.systemName ?? selectedSystem?.label ?? '-');
    const registryId = Number(selectedSystem?.data?.templateId ?? null);
    this.selectedSystemTemplateId = Number.isFinite(registryId) && registryId > 0 ? registryId : null;
    this.#selectedSystemHasTemplate = selectedSystem?.data?.templateId != null;

    if (this.selectedSystemTemplateId !== null) {
      this.#validateTemplateAndRefreshCustomFields(selectedSystemId, this.selectedSystemTemplateId);
      return;
    }

    this.#validateTemplateAndRefreshCustomFields(selectedSystemId, null);

    this.#lastRequestedSystemUniqueId = selectedSystemId;
    this.#modalActionPlanService
      .getSystemById(selectedSystemId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (system) => {
          if (this.#lastRequestedSystemUniqueId !== selectedSystemId) return;

          this.#resolvedSystemLabel = String(system?.systemName ?? '-').trim() || '-';
          const selectedTemplateId = Number(system?.templateId ?? null);
          this.selectedSystemTemplateId = Number.isFinite(selectedTemplateId) && selectedTemplateId > 0 ? selectedTemplateId : null;
          this.#selectedSystemHasTemplate = system?.templateId != null;
          this.#validateTemplateAndRefreshCustomFields(selectedSystemId, this.selectedSystemTemplateId);
        },
        error: () => {
          if (this.#lastRequestedSystemUniqueId !== selectedSystemId) return;
          this.selectedSystemTemplateId = null;
          this.#selectedSystemHasTemplate = false;
          this.shouldShowCustomFields = false;
          this.#resolvedSystemLabel = '-';
        },
      });
  }

  #validateTemplateAndRefreshCustomFields(selectedSystemId: number, templateId: number | null): void {
    if (!templateId) {
      this.#lastTemplateValidationSystemUniqueId = selectedSystemId;
      this.shouldShowCustomFields = false;
      this.isResolvingCustomFieldsTemplate = false;
      return;
    }

    this.#lastTemplateValidationSystemUniqueId = selectedSystemId;
    this.shouldShowCustomFields = false;
    this.isResolvingCustomFieldsTemplate = true;
    this.#modalActionPlanService
      .isCustomFieldTemplateActive(templateId, this.applicationId)
      .pipe(
        finalize(() => {
          if (this.#lastTemplateValidationSystemUniqueId === selectedSystemId) {
            this.isResolvingCustomFieldsTemplate = false;
          }
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((isTemplateActive) => {
        if (this.#lastTemplateValidationSystemUniqueId !== selectedSystemId) return;
        this.shouldShowCustomFields = !!isTemplateActive;
        this.#refreshCustomFieldsFromCurrentSystem();
      });
  }

  #resolveSelectedSystemId(value: number[] | number | string | null): number | null {
    if (Array.isArray(value)) {
      const first = value?.[0];
      const parsed = Number(first);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  #applyNotificationParams(): void {
    if (!this.isAddMode) return;
    const params = this.notificationParams();
    if (!params) return;

    const matchedSystem = findSystemByApplicationId(this.optionsCombos?.systems, params.applicationId);
    if (matchedSystem) {
      this.formActionPlan?.controls?.systemId?.setValue([matchedSystem.value], { emitEvent: true });
      this.isSystemLockedByWidget = true;
      this.formActionPlan?.controls?.systemId?.disable({ emitEvent: false });
    }

    const widgetSourceLabel = resolveWidgetSourceLabel(params);
    if (widgetSourceLabel !== null) {
      this.sourceLabel = widgetSourceLabel;
    }
  }

  #refreshCustomFieldsFromCurrentSystem(): void {
    if (!this.shouldShowCustomFields) return;
    if (!this.actionPlanUniqueId) return;
    if (this.#consumeCustomFieldsReloadSuppression()) return;

    // Wait for the custom-field component instance to be recreated when system/template changes.
    setTimeout(() => this.#loadCustomFieldsByActionPlanId());
  }

  #applyModeRules(): void {
    if (!this.formActionPlan) return;

    this.dueDateMin = this.isAddMode ? this.#startOfToday() : undefined;

    if (this.isAddMode) {
      this.formActionPlan.enable({ emitEvent: false });
      this.formActionPlan.controls.creationDate.disable({ emitEvent: false });
      this.formActionPlan.controls.approvalDateText.disable({ emitEvent: false });
      this.formActionPlan.controls.conclusionDateText.disable({ emitEvent: false });
      if (this.isSystemLockedByWidget) {
        this.formActionPlan.controls.systemId.disable({ emitEvent: false });
      }
      return;
    }

    if (this.isEditMode) {
      this.formActionPlan.enable({ emitEvent: false });
      this.formActionPlan.controls['statusId'].disable({ emitEvent: false });
      this.formActionPlan.controls['creationDate'].disable({ emitEvent: false });
      this.formActionPlan.controls['dueDate'].disable({ emitEvent: false });
      this.formActionPlan.controls['approvalDateText'].disable({ emitEvent: false });
      this.formActionPlan.controls['conclusionDateText'].disable({ emitEvent: false });
      this.formActionPlan.controls['responsibleId'].disable({ emitEvent: false });

      if (this.isSystemLockedInEditMode) {
        this.formActionPlan.controls.systemId.disable({ emitEvent: false });
      }

      if (!this.canEditOriginator) {
        this.formActionPlan.controls.originatorId.disable({ emitEvent: false });
      }
      return;
    }

    this.formActionPlan.disable({ emitEvent: false });
  }

  #startOfToday(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  /**
   * Pre-selects the System whose GUID (`systemId`) matches the given
   * applicationId and locks (disables) the field. Used by embed flows that
   * must force the action plan into a specific system. Safe to call before
   * combos finish loading — it re-applies once {@link #loadCombos} resolves.
   */
  public lockSystemByApplicationId(applicationId: string | null | undefined): void {
    this.#lockedSystemApplicationId = String(applicationId ?? '').trim() || null;
    this.#applyLockedSystem();
  }

  /**
   * Pre-selects the System by its `systemUniqueId` (the combo option value) and
   * locks (disables) the field. Used by host module launchers that resolve the
   * system via the `System/by-application` lookup. Safe to call before combos
   * finish loading — it re-applies once {@link #loadCombos} resolves.
   */
  public lockSystemByUniqueId(systemUniqueId: number | null | undefined): void {
    const parsed = Number(systemUniqueId);
    this.#lockedSystemUniqueId = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    this.#applyLockedSystem();
  }

  #applyLockedSystem(): void {
    if (!this.optionsCombos?.systems?.length) return;
    const control = this.formActionPlan?.controls?.systemId;
    if (!control) return;

    const option = this.#resolveLockedSystemOption();
    if (!option) return;

    control.setValue([option.value], { emitEvent: true });
    control.disable({ emitEvent: false });
    this.selectedSystemUniqueId = Number(option.value) || null;
  }

  #resolveLockedSystemOption(): ISvcChipOption<SvcModalActionPlanSystemData> | undefined {
    if (this.#lockedSystemUniqueId) {
      return this.optionsCombos.systems.find((system) => Number(system?.value) === this.#lockedSystemUniqueId);
    }
    if (this.#lockedSystemApplicationId) {
      const target = this.#lockedSystemApplicationId.toLowerCase();
      return this.optionsCombos.systems.find((system) => String(system?.data?.systemId ?? '').toLowerCase() === target);
    }
    return undefined;
  }

  public setCreatedState(response: SvcModalActionPlanCreateResponse | number): void {
    const statusId = this.#resolveStatusId(response);
    this.formActionPlan.controls.statusId.setValue(statusId, { emitEvent: false });
    this.mode.set(this.modalModes.view);
    this.#applyModeRules();
  }

  public patchFormActionPlan(actionPlan: SvcModalActionPlanByIdResponse): void {
    if (!actionPlan || !this.formActionPlan) return;
    this.#lastPatchedActionPlan = actionPlan;

    const originatorId = actionPlan?.originatorUserId ?? null;
    const dueDate = actionPlan?.actualPrevisionDate ?? actionPlan?.initialPrevisionDate ?? null;
    const statusId = this.#resolveStatusIdFromActionPlan(actionPlan);
    const resolvedOwners = this.#resolveEncodedOwnersFromActionPlan(actionPlan);
    const currentActors = (this.formActionPlan?.controls?.actors?.value ?? []) as string[];

    this.actionPlanUniqueId = Number(actionPlan?.actionPlanUniqueId ?? 0) || null;
    this.actionPlanNumber = this.#resolveActionPlanNumber(actionPlan?.actionPlanId);
    this.sourceLabel = this.#resolveSourceLabel(actionPlan);
    this.#setSourceLinkDisplay(actionPlan);
    this.reprogrammingLabel = this.#resolveReprogrammingLabel(actionPlan?.quantityReprogramming);
    this.isSystemLockedInEditMode = this.#resolveSystemLockInEditMode(actionPlan);

    this.formActionPlan.patchValue(
      {
        description: actionPlan?.actionPlanName ?? null,
        statusId,
        typeId: this.#toSingleSelectionValueByOptions(actionPlan?.typeId, this.optionsCombos?.types),
        priorityId: this.#toSingleSelectionValueByOptions(actionPlan?.priorityUniqueId, this.optionsCombos?.priorities),
        systemId: this.#toSingleSelectionValueByOptions(actionPlan?.systemUniqueId, this.optionsCombos?.systems),
        originatorId: this.#toSingleSelectionValueByOptions(originatorId, this.usersOptions),
        responsibleId: this.#toSingleSelectionValueByOptions(actionPlan?.responsibleUserId, this.usersOptions),
        actors: this.#toMultipleSelectionValuesByOptions(
          resolvedOwners ?? currentActors,
          this.#flattenGroupedOptions(this.ownersOptions),
        ),
        confidential: !!actionPlan?.isClassified,
        approverIds: this.#toMultipleSelectionValuesByOptions(
          this.#permissions.getApproverIds(actionPlan),
          this.usersOptions
        ),
        creationDate: this.#formatDateToControl(actionPlan?.creationDate),
        dueDate: this.#formatDateToControl(dueDate),
      },
      { emitEvent: false }
    );

    this.setApprovalDate(actionPlan?.approvalDate);
    this.setConclusionDate(actionPlan?.conclusionDate);
    this.#syncOriginatorDisplayFromForm();
    this.#setSystemTemplateId(this.formActionPlan?.controls?.systemId?.value);
    this.#refreshDateFieldsComponent();
    this.#refreshCustomFieldsComponent();
    this.#reloadCustomFieldsAfterComponentRefresh();
    this.#applyModeRules();
    this.formActionPlan.markAsPristine();
    this.formActionPlan.markAsUntouched();
  }

  public get approverIdsControl(): FormControl {
    return this.formActionPlan?.controls?.['approverIds'] as FormControl;
  }

  public suppressNextCustomFieldsReload(times = 1): void {
    const normalizedTimes = Math.max(0, Math.trunc(Number(times) || 0));
    this.#suppressedCustomFieldReloads += normalizedTimes;
  }

  public applyCopyHeaderData(copyData: SvcModalActionPlanCopyHeaderData): void {
    if (!this.formActionPlan) return;

    this.mode.set(this.modalModes.add);
    this.actionPlanUniqueId = null;
    this.actionPlanNumber = `${this.#actionPlanPrefix()}-`;
    this.sourceLabel = '-';
    this.reprogrammingLabel = '-';
    this.isSystemLockedInEditMode = false;
    this.#lastPatchedActionPlan = null;
    this.#resolvedSystemLabel = '-';

    const today = this.#datePipe.transform(new Date(), 'yyyy-MM-dd');
    const currentUserId = this.#userService.userId$ ?? null;

    this.formActionPlan.patchValue(
      {
        description: copyData?.description ?? null,
        statusId: null,
        typeId: this.#toSingleSelectionValueByOptions(copyData?.typeId, this.optionsCombos?.types),
        priorityId: this.#toSingleSelectionValueByOptions(copyData?.priorityId, this.optionsCombos?.priorities),
        systemId: this.#toSingleSelectionValueByOptions(copyData?.systemId, this.optionsCombos?.systems),
        originatorId: this.#toSingleSelectionValueByOptions(currentUserId, this.usersOptions),
        responsibleId: this.#toSingleSelectionValueByOptions(copyData?.responsibleId, this.usersOptions),
        actors: this.#toMultipleSelectionValuesByOptions(
          encodeOwnerSelection(copyData?.actors ?? [], []),
          this.#flattenGroupedOptions(this.ownersOptions),
        ),
        confidential: !!copyData?.confidential,
        creationDate: today,
        dueDate: null,
      },
      { emitEvent: false }
    );

    this.setApprovalDate(null);
    this.setConclusionDate(null);

    this.#syncOriginatorDisplayFromForm();
    this.#setSystemTemplateId(this.formActionPlan?.controls?.systemId?.value);
    this.#refreshDateFieldsComponent();
    this.#refreshCustomFieldsComponent();
    this.#applyModeRules();
  }

  #resolveStatusId(response: SvcModalActionPlanCreateResponse | number): number {
    if (typeof response !== 'number' && Number(response?.statusId)) {
      return Number(response?.statusId);
    }
    return SvcModalActionPlanStatusId.NoPrazo;
  }

  public setApprovalDate(value?: string | null): void {
    const label = value ? this.#formatDate(value) : '-';
    this.formActionPlan.controls.approvalDateText.setValue(label, { emitEvent: false });
  }

  public setConclusionDate(value?: string | null): void {
    const label = value ? this.#formatDate(value) : '-';
    this.formActionPlan.controls.conclusionDateText.setValue(label, { emitEvent: false });
  }

  #formatDate(value: string): string {
    return this.#datePipe.transform(value, this.#appSettings.dateFormat) ?? '-';
  }

  #formatDateToControl(value?: string | null): string | null {
    if (!value) return null;
    return this.#datePipe.transform(value, 'yyyy-MM-dd') ?? null;
  }

  #toSingleSelectionValueByOptions(
    value: string | number | null | undefined,
    options?: ISvcChipOption[]
  ): Array<string | number> {
    if (value === null || value === undefined || value === '') return [];

    const matchedOption = (options ?? []).find((option) => `${option?.value ?? ''}` === `${value}`);
    if (matchedOption?.value !== undefined && matchedOption?.value !== null) {
      return [matchedOption.value as string | number];
    }

    return [value];
  }

  #toMultipleSelectionValuesByOptions(values: Array<string | number>, options?: ISvcChipOption[]): Array<string | number> {
    return (values ?? []).flatMap((value) => this.#toSingleSelectionValueByOptions(value, options));
  }

  #syncSelectionControlsWithLoadedOptions(): void {
    if (!this.formActionPlan) return;

    if (this.#lastPatchedActionPlan) {
      this.#patchSelectionControlsFromActionPlan(this.#lastPatchedActionPlan);
      return;
    }

    const rawValue = this.formActionPlan.getRawValue();
    this.formActionPlan.patchValue(
      {
        typeId: this.#toSingleSelectionValueByOptions(rawValue?.typeId?.[0] ?? null, this.optionsCombos?.types),
        priorityId: this.#toSingleSelectionValueByOptions(rawValue?.priorityId?.[0] ?? null, this.optionsCombos?.priorities),
        systemId: this.#toSingleSelectionValueByOptions(rawValue?.systemId?.[0] ?? null, this.optionsCombos?.systems),
        originatorId: this.#toSingleSelectionValueByOptions(rawValue?.originatorId?.[0] ?? null, this.usersOptions),
        responsibleId: this.#toSingleSelectionValueByOptions(rawValue?.responsibleId?.[0] ?? null, this.usersOptions),
        actors: this.#toMultipleSelectionValuesByOptions(
          rawValue?.actors ?? [],
          this.#flattenGroupedOptions(this.ownersOptions),
        ),
        approverIds: this.#toMultipleSelectionValuesByOptions(rawValue?.approverIds ?? [], this.usersOptions),
      },
      { emitEvent: false }
    );
  }

  #patchSelectionControlsFromActionPlan(actionPlan: SvcModalActionPlanByIdResponse): void {
    const resolvedOwners = this.#resolveEncodedOwnersFromActionPlan(actionPlan);
    const currentActors = (this.formActionPlan?.controls?.actors?.value ?? []) as string[];

    this.formActionPlan.patchValue(
      {
        typeId: this.#toSingleSelectionValueByOptions(actionPlan?.typeId, this.optionsCombos?.types),
        priorityId: this.#toSingleSelectionValueByOptions(actionPlan?.priorityUniqueId, this.optionsCombos?.priorities),
        systemId: this.#toSingleSelectionValueByOptions(actionPlan?.systemUniqueId, this.optionsCombos?.systems),
        originatorId: this.#toSingleSelectionValueByOptions(actionPlan?.originatorUserId, this.usersOptions),
        responsibleId: this.#toSingleSelectionValueByOptions(actionPlan?.responsibleUserId, this.usersOptions),
        actors: this.#toMultipleSelectionValuesByOptions(
          resolvedOwners ?? currentActors,
          this.#flattenGroupedOptions(this.ownersOptions),
        ),
        approverIds: this.#toMultipleSelectionValuesByOptions(
          this.#permissions.getApproverIds(actionPlan),
          this.usersOptions
        ),
      },
      { emitEvent: false }
    );

    this.#resolveMissingUserNames(actionPlan);
  }

  #resolveApproverIdsFromActionPlan(actionPlan: SvcModalActionPlanByIdResponse | null | undefined): string[] {
    if (!Array.isArray(actionPlan?.approverUsers)) return [];
    return this.#normalizeActorIds(actionPlan?.approverUsers ?? []);
  }

  #resolveMissingUserNames(actionPlan: SvcModalActionPlanByIdResponse): void {
    const originatorId = actionPlan?.originatorUserId ?? null;
    const responsibleId = actionPlan?.responsibleUserId ?? null;
    const idsToFetch: string[] = [];

    if (originatorId && !this.#resolveUserLabel(originatorId)) idsToFetch.push(originatorId);
    if (responsibleId && responsibleId !== originatorId && !this.#resolveUserLabel(responsibleId)) idsToFetch.push(responsibleId);

    if (!idsToFetch.length) return;

    this.#userService.getUsersInfoById(idsToFetch).pipe(
      takeUntilDestroyed(this.#destroyRef)
    ).subscribe({
      next: (users) => {
        const findName = (id: string): string =>
          users.find((u) => `${u?.userId ?? ''}`.toUpperCase() === id.toUpperCase())?.name ?? '';

        if (originatorId && !this.#resolveUserLabel(originatorId)) {
          this.#apiOriginatorName = findName(originatorId);
          this.#syncOriginatorDisplayFromForm();
        }
        if (responsibleId && !this.#resolveUserLabel(responsibleId)) {
          this.#apiResponsibleName = findName(responsibleId);
        }
      },
    });
  }

  #toNullableNumber(value: string | number | null | undefined): number | null {
    const parsedValue = Number(value ?? null);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  #resolveSingleSelectionValue(
    value: string | number | Array<string | number> | null | undefined,
    fallback: string | number | null | undefined = null
  ): string | number | null {
    if (Array.isArray(value)) {
      const firstValue = value.find((item) => item !== null && item !== undefined && item !== '');
      return firstValue ?? (fallback ?? null);
    }

    if (value !== null && value !== undefined && value !== '') {
      return value;
    }

    return fallback ?? null;
  }

  #resolveStatusIdFromActionPlan(actionPlan: SvcModalActionPlanByIdResponse): number | null {
    const parsedStatusId = Number(actionPlan?.statusId ?? actionPlan?.phaseId ?? NaN);
    if (!Number.isFinite(parsedStatusId) || !this.statusLabelById?.[parsedStatusId]) {
      return null;
    }
    return parsedStatusId;
  }
  #resolveActionPlanNumber(actionPlanId?: number | null): string {
    const prefix = this.#actionPlanPrefix();
    const parsedId = Number(actionPlanId ?? 0);
    if (!Number.isFinite(parsedId) || parsedId <= 0) {
      return `${prefix}-`;
    }

    return `${prefix}-${Math.trunc(parsedId)}`;
  }

  /** Centralised prefix lookup so every render path uses the same transloco key. */
  #actionPlanPrefix(): string {
    return this.#translocoService.translate('PLA');
  }

  #resolveSourceLabel(actionPlan: SvcModalActionPlanByIdResponse): string {
    // `sourceDesc` carries the parent record number (e.g. `BOS-1301`, `UNC-166`)
    // — it is the field the API actually returns; the others are fallbacks.
    const source = actionPlan?.sourceDesc ?? actionPlan?.sourceDescription ?? actionPlan?.sourceName ?? actionPlan?.source ?? '';
    const label = String(source ?? '').trim();
    if (!label || isDraftSourceRef(label)) return '-';
    return label;
  }

  #setSourceLinkDisplay(actionPlan: SvcModalActionPlanByIdResponse): void {
    if (isDraftSourceRef(actionPlan?.sourceDesc)) {
      this.showSourceLink = false;
      this.canOpenSourceLink = false;
      return;
    }

    // Revamp parents (BOS, DFT, UNC, RCA, Alert) are detected by the `sourceDesc`
    // prefix and open their own modal — so the "Fonte" stays a clickable link
    // even when the backend `sourceLink` is empty/legacy.
    const registry = parseActionPlanSourceRegistry(actionPlan?.sourceDesc);
    const isRevampParent = !!registry && this.#sourceRedirect.isRevampPrefix(registry.prefix);
    const hasSourceLink = !!actionPlan?.sourceLink && !!this.#sourceLinkRedirect.parse(actionPlan.sourceLink);
    this.showSourceLink = isRevampParent || hasSourceLink;
    this.canOpenSourceLink = isRevampParent || hasSourceLink;
  }

  public openSourceLink(): void {
    // Delegates to the shared redirect service so the modal and the search list
    // open a given source identically (AB#68126).
    void this.#sourceRedirect.openSource(this.#lastPatchedActionPlan?.sourceDesc, this.#lastPatchedActionPlan?.sourceLink);
  }

  #resolveReprogrammingLabel(quantityReprogramming?: number | null): string {
    if (quantityReprogramming === null || quantityReprogramming === undefined) return '-';
    const parsedQuantity = Number(quantityReprogramming);
    return Number.isFinite(parsedQuantity) ? `${parsedQuantity}` : '-';
  }

  #resolveSystemLockInEditMode(actionPlan: SvcModalActionPlanByIdResponse): boolean {
    if (actionPlan?.isSystemInternal === true) return true;

    const explicitFlag = actionPlan?.isSystemLockedForEdit ?? actionPlan?.lockSystemOnEdit;
    if (typeof explicitFlag === 'boolean') return explicitFlag;

    return !!actionPlan?.sourceApplicationId;
  }

  #resolveActorIds(actionPlan?: SvcModalActionPlanByIdResponse | null): string[] | null {
    const actorsFromApi = [actionPlan?.userOwners, actionPlan?.actorUserIds, actionPlan?.actorsUserIds, actionPlan?.actors].find((entry) => Array.isArray(entry));
    if (!Array.isArray(actorsFromApi)) return null;

    return this.#normalizeActorIds(actorsFromApi);
  }

  #resolveEncodedOwnersFromActionPlan(actionPlan?: SvcModalActionPlanByIdResponse | null): string[] | null {
    const resolvedUsers = this.#resolveActorIds(actionPlan);
    const teamOwnersFromApi = actionPlan?.teamOwners;
    const hasTeams = Array.isArray(teamOwnersFromApi);
    if (resolvedUsers === null && !hasTeams) return null;

    return encodeOwnerSelection(resolvedUsers ?? [], hasTeams ? teamOwnersFromApi : []);
  }

  #normalizeActorIds(actorIds: Array<string | number | null | undefined>): string[] {
    const normalizedActorIds = (Array.isArray(actorIds) ? actorIds : [])
      .map((actorId) => String(actorId ?? '').trim())
      .filter((actorId) => !!actorId);

    return Array.from(new Set(normalizedActorIds));
  }
  #flattenGroupedOptions(groups?: ISvcChipGroupedOption[] | null): ISvcChipOption[] {
    return (groups ?? []).flatMap((group) => (group?.options ?? []) as ISvcChipOption[]);
  }

  #syncOriginatorDisplayFromForm(): void {
    const originatorId = this.formActionPlan?.controls?.originatorId?.value?.[0];
    const resolvedName =
      this.#resolveUserLabel(originatorId) ||
      this.#apiOriginatorName ||
      (this.isAddMode ? this.#userService.user?.firstLastName : '') ||
      '';
    if (!resolvedName) return;

    this.originatorName = resolvedName;
  }

  #resolveUserLabel(userId?: string | null): string {
    if (!userId) return '';
    const option = this.optionsCombos?.users?.find((user) => `${user?.value ?? ''}`.toUpperCase() === `${userId}`.toUpperCase());
    if (option?.label === undefined || option?.label === null) return '';
    return String(option.label);
  }
  #resolveTeamLabel(teamId?: string | number | null): string {
    const numericId = Number(teamId);
    if (!Number.isFinite(numericId) || numericId === 0) return '';
    const option = this.optionsCombos?.teams?.find((team) => Number(team?.value) === numericId);
    if (option?.label === undefined || option?.label === null) return '';
    return String(option.label);
  }

  #resolveOptionLabel(value: string | number | null | undefined, options?: ISvcChipOption[]): string {
    if (value === null || value === undefined || value === '') return '-';

    const option = (options ?? []).find((item) => `${item?.value ?? ''}` === `${value}`);
    if (option?.label === undefined || option?.label === null || option?.label === '') {
      return '-';
    }

    return String(option.label);
  }

  #resolveTypeFallbackLabel(typeId?: number | null): string {
    const labelsById: Record<number, string> = {
      1: this.#translocoService.translate('Corretivo'),
      2: this.#translocoService.translate('Preventivo'),
      3: this.#translocoService.translate('Outros'),
    };

    return labelsById[Number(typeId ?? NaN)] ?? '-';
  }

  #resolvePriorityFallbackLabel(priorityId?: number | null): string {
    const labelsById: Record<number, string> = {
      1: this.#translocoService.translate('Alta'),
      2: this.#translocoService.translate('Media'),
      3: this.#translocoService.translate('Baixa'),
    };

    return labelsById[Number(priorityId ?? NaN)] ?? '-';
  }

  #consumeCustomFieldsReloadSuppression(): boolean {
    if (this.#suppressedCustomFieldReloads <= 0) return false;

    this.#suppressedCustomFieldReloads--;
    return true;
  }

  #refreshCustomFieldsComponent(): void {
    this.customFieldsRefreshTick.update((value) => value + 1);
  }

  #refreshDateFieldsComponent(): void {
    this.dateFieldsRefreshTick.update((value) => value + 1);
  }

  #reloadCustomFieldsAfterComponentRefresh(): void {
    setTimeout(() => this.#loadCustomFieldsByActionPlanId());
  }

  #loadCustomFieldsByActionPlanId(): void {
    if (!this.shouldShowCustomFields) return;
    if (this.#consumeCustomFieldsReloadSuppression()) return;
    if (!this.actionPlanUniqueId || !this.customField?.getCustomFieldByExternalReferenceId) return;
    this.customField.getCustomFieldByExternalReferenceId(this.selectedSystemTemplateId ?? null, this.actionPlanUniqueId);
  }

  public get usersOptions(): ISvcChipOption[] {
    return this.optionsCombos?.users ?? [];
  }

  public get ownersOptions(): ISvcChipGroupedOption[] {
    return this.optionsCombos?.owners ?? [];
  }

  public get selectedApproverIds(): string[] {
    const rawApprovers = this.formActionPlan?.getRawValue()?.approverIds;
    const fromForm = this.#resolveMultipleUserSelectionIds(rawApprovers);
    if (fromForm.length) return fromForm;
    return this.#permissions.getApproverIds(this.#lastPatchedActionPlan);
  }

  public get selectedApproverLabels(): string[] {
    return this.selectedApproverIds.map((id) => this.#resolveUserLabel(id) || '');
  }

  public get hasHeaderChanges(): boolean {
    if (!this.formActionPlan?.controls) return false;
    return Object.values(this.formActionPlan.controls).some((control) => !!control?.dirty);
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public markAllAsTouchedForms(): void {
    this.formActionPlan?.markAllAsTouched();
    this.customField?.formCustomFields?.markAllAsTouched();
  }

  public get validForms(): boolean {
    const customFieldsForm = this.customField?.formCustomFields;
    const isCustomFieldsValid = !customFieldsForm || customFieldsForm.disabled || customFieldsForm.valid;
    return this.formActionPlan?.valid && isCustomFieldsValid;
  }

  public hasCustomFields(): boolean {
    return this.customField?.hasFields?.() ?? false;
  }

  public saveCustomFields(recordId: number): Observable<boolean> {
    const customField = this.customField;
    if (!customField || !customField.hasFields?.() || !recordId) return of(true);
    return customField.changeCustomFieldRecord(recordId);
  }

  public get formValue(): SvcModalActionPlanCreateRequest {
    const value = this.formActionPlan?.getRawValue();
    const approverUsers = this.#resolveMultipleUserSelectionIds(value?.approverIds);
    // The Atores chip-grouped control stores prefixed values (`user:<guid>`
    // and `team:<id>`). Split them at the form boundary so the payload keeps
    // the legacy `actorIds` (→ `userOwners`) contract and gains `teamOwners`.
    const ownerSelection = decodeOwnerSelection(value?.actors ?? []);

    return {
      title: value?.description?.trim?.() ?? '',
      typeId: this.#toNullableNumber(value?.typeId?.[0]),
      priorityId: this.#toNullableNumber(value?.priorityId?.[0]),
      systemId: this.#toNullableNumber(value?.systemId?.[0]),
      originatorId: value?.originatorId?.[0] ?? this.#userService.userId$ ?? null,
      responsibleId: value?.responsibleId?.[0] ?? null,
      approverUsers,
      actorIds: ownerSelection.userOwners,
      teamOwners: ownerSelection.teamOwners,
      expectedDate: value?.dueDate ?? null,
      // AB#74012 send the local creation day; without it the service fell back to the UTC day
      creationDate: value?.creationDate ?? null,
      confidential: !!value?.confidential,
      siteId: this.#modalActionPlanService.getCurrentSiteId(),
    };
  }

  #resolveSingleUserSelectionId(
    value: string | number | Array<string | number | null | undefined> | null | undefined,
    fallback: string | null = null
  ): string | null {
    if (Array.isArray(value)) {
      const normalizedValues = this.#normalizeActorIds(value);
      return normalizedValues[normalizedValues.length - 1] ?? fallback;
    }

    const normalizedValue = String(value ?? '').trim();
    return normalizedValue || fallback;
  }

  #resolveMultipleUserSelectionIds(
    value: string | number | Array<string | number | null | undefined> | null | undefined,
  ): string[] {
    if (Array.isArray(value)) {
      return this.#normalizeActorIds(value);
    }

    const normalizedValue = String(value ?? '').trim();
    return normalizedValue ? [normalizedValue] : [];
  }
}